// AdminUsersPage.js - /admin/users
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import Layout from '../Layout';
import { AdminLoadingBar, AdminLoadingStyles, AdminSpinner } from './AdminLoadingUi';

async function adminFetch(path, opts = {}) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(path, { ...opts, headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export default function AdminUsersPage() {
  const BUSY_DOTS = '\u002E\u002E\u002E';
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({}); // phone -> true while action in flight

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await adminFetch(`/api/admin/users${params}`);
      setUsers(data.users);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  async function blockToggle(phone, currentlyBlocked) {
    const action = currentlyBlocked ? 'unblock' : 'block';
    const confirmMsg = currentlyBlocked
      ? `Unblock ${phone}?`
      : `Block ${phone}? They will get a 403 on all data requests within 5 minutes.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy((b) => ({ ...b, [phone]: true }));
    try {
      await adminFetch(`/api/admin/users/${encodeURIComponent(phone)}/${action}`, { method: 'POST' });
      setUsers((prev) => prev.map((u) => (u.phone === phone ? { ...u, blocked: !currentlyBlocked } : u)));
    } catch (e) {
      alert('Failed: ' + e.message);
    }
    setBusy((b) => {
      const n = { ...b };
      delete n[phone];
      return n;
    });
  }

  async function revokeAll(phone) {
    if (!window.confirm(`Force logout ${phone} from ALL devices? They will need to log in again.`)) return;
    setBusy((b) => ({ ...b, [phone]: true }));
    try {
      const data = await adminFetch(`/api/admin/users/${encodeURIComponent(phone)}/revoke-all`, { method: 'POST' });
      alert(data.message || 'Done');
    } catch (e) {
      alert('Failed: ' + e.message);
    }
    setBusy((b) => {
      const n = { ...b };
      delete n[phone];
      return n;
    });
  }

  const page = { fontFamily: 'sans-serif', padding: '28px 32px', maxWidth: 1200, margin: '0 auto', color: '#1e293b' };
  const btn = (color) => ({
    fontSize: 12,
    padding: '4px 10px',
    border: `1px solid ${color}`,
    borderRadius: 4,
    cursor: 'pointer',
    background: '#fff',
    color,
  });

  return (
    <Layout>
      <div style={page}>
        <AdminLoadingStyles />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <Link to="/admin" style={{ color: '#64748b', textDecoration: 'none', fontSize: 14 }}>
            {'← Admin Panel'}
          </Link>
          <h1 style={{ margin: 0, fontSize: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>Users</span>
            <AdminSpinner loading={loading} />
          </h1>
          <span style={{ color: '#64748b', fontSize: 13 }}>{users.length} shown</span>
        </div>
        <AdminLoadingBar loading={loading} />

        <input
          style={{
            width: '100%',
            maxWidth: 340,
            padding: '8px 12px',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            fontSize: 14,
            marginBottom: 20,
            boxSizing: 'border-box',
          }}
          placeholder="Search by phone or plan..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>Error: {error}</div>}

        {!loading && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                {['Phone', 'Plan', 'Status', 'Expires', 'Blocked', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.phone} style={{ borderTop: '1px solid #e2e8f0', background: u.blocked ? '#fff7ed' : 'transparent' }}>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    <Link to={`/admin/users/${encodeURIComponent(u.phone)}`} style={{ color: '#3b82f6' }}>
                      {u.phone}
                    </Link>
                  </td>
                  <td style={{ padding: '9px 12px', textTransform: 'capitalize' }}>{u.plan}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ color: u.isActive ? '#16a34a' : '#94a3b8', fontWeight: 600 }}>
                      {u.isActive ? 'Active' : 'Expired'}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 12 }}>
                    {u.endDate ? new Date(u.endDate).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    {u.blocked ? (
                      <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 12 }}>BLOCKED</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Link
                        to={`/admin/users/${encodeURIComponent(u.phone)}`}
                        style={{ ...btn('#3b82f6'), textDecoration: 'none', padding: '4px 10px', display: 'inline-block' }}
                      >
                        Activity
                      </Link>
                      <button disabled={!!busy[u.phone]} onClick={() => blockToggle(u.phone, u.blocked)} style={btn(u.blocked ? '#16a34a' : '#dc2626')}>
                        {busy[u.phone] ? BUSY_DOTS : u.blocked ? 'Unblock' : 'Block'}
                      </button>
                      <button disabled={!!busy[u.phone]} onClick={() => revokeAll(u.phone)} style={btn('#7c3aed')}>
                        {busy[u.phone] ? BUSY_DOTS : 'Force Logout'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
